import React, { useState } from "react";
import { db } from "../firebase/config";
import { doc, updateDoc } from "firebase/firestore";
import { useAuth } from "../context/AuthContext";
import { useTheme } from "../context/ThemeContext";
import { registrarAccion } from "../utils/trazabilidad";

const TERMINOS_URL = "https://firebasestorage.googleapis.com/v0/b/deluxe-software.firebasestorage.app/o/Legales%2FDeluxe_Software_Terminos_y_Condiciones.pdf?alt=media&token=a60e77a0-f51e-4174-9993-1571db0cf26c";

export default function OnboardingWizard({ onCompletado }) {
  const { currentUser, userRole, clinicaId, clinicaData } = useAuth();
  const { theme } = useTheme();
  const [paso, setPaso] = useState(1);
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState('');

  const [datosClinica, setDatosClinica] = useState({
    nombre: clinicaData?.nombre || '',
    telefono: clinicaData?.telefono || '',
    web: clinicaData?.web || '',
    direccion: clinicaData?.direccion || '',
    ciudad: clinicaData?.ciudad || '',
    provincia: clinicaData?.provincia || '',
    condicionImpositiva: clinicaData?.condicionImpositiva || 'monotributo',
    cuitCuil: clinicaData?.cuitCuil || '',
  });

  const [medicos, setMedicos] = useState({
    medico1: clinicaData?.medicos?.medico1 || '',
    medico2: clinicaData?.medicos?.medico2 || '',
    medico3: clinicaData?.medicos?.medico3 || '',
    medico4: clinicaData?.medicos?.medico4 || '',
  });

  const [terminosAceptados, setTerminosAceptados] = useState(false);

  const inputStyle = { width: '100%', padding: '11px 14px', fontSize: '13px', border: `0.5px solid ${theme.borderInput}`, borderRadius: '8px', outline: 'none', color: theme.primary, background: theme.bgInput, boxSizing: 'border-box', fontFamily: "'Montserrat', sans-serif" };
  const labelStyle = { fontSize: '11px', color: theme.secondary, display: 'block', marginBottom: '6px', fontWeight: '500', letterSpacing: '1px' };
  const btnPrimaryStyle = { background: `linear-gradient(135deg, ${theme.accent}, ${theme.accentDark})`, border: 'none', borderRadius: '8px', padding: '12px 28px', fontSize: '14px', cursor: 'pointer', color: '#FFFFFF', fontWeight: '700', fontFamily: "'Montserrat', sans-serif" };
  const btnSecondaryStyle = { background: 'transparent', border: `0.5px solid ${theme.borderInput}`, borderRadius: '8px', padding: '12px 28px', fontSize: '14px', cursor: 'pointer', color: theme.secondary, fontFamily: "'Montserrat', sans-serif" };

  function validarPaso1() {
    if (!datosClinica.nombre.trim()) return 'El nombre de la clínica es obligatorio';
    if (!datosClinica.telefono.trim()) return 'El teléfono es obligatorio';
    if (!datosClinica.direccion.trim()) return 'La dirección es obligatoria';
    if (!datosClinica.ciudad.trim()) return 'La ciudad es obligatoria';
    if (!datosClinica.provincia.trim()) return 'La provincia es obligatoria';
    const cuit = datosClinica.cuitCuil.replace(/\D/g, '');
    if (cuit.length !== 11) return 'El CUIT/CUIL debe tener 11 dígitos';
    return null;
  }

  function validarPaso2() {
    if (!medicos.medico1.trim()) return 'Ingresá al menos el nombre del médico principal';
    return null;
  }

  async function guardarPaso1Y2() {
    setGuardando(true);
    try {
      await updateDoc(doc(db, 'clinicas', clinicaId), {
        nombre: datosClinica.nombre,
        telefono: datosClinica.telefono,
        web: datosClinica.web,
        direccion: datosClinica.direccion,
        ciudad: datosClinica.ciudad,
        provincia: datosClinica.provincia,
        condicionImpositiva: datosClinica.condicionImpositiva,
        cuitCuil: datosClinica.cuitCuil.replace(/\D/g, ''),
        medicos,
        onboardingPaso: 3,
      });
      setPaso(3);
    } catch (e) { setError('Error al guardar: ' + e.message); }
    setGuardando(false);
  }

  async function finalizarOnboarding() {
    if (!terminosAceptados) { setError('Debés aceptar los términos y condiciones para continuar'); return; }
    setGuardando(true);
    try {
      await updateDoc(doc(db, 'clinicas', clinicaId), {
        onboardingCompletado: true,
        terminosAceptados: true,
        terminosAceptadosEn: new Date(),
        terminosAceptadosPor: currentUser?.email,
      });
      await registrarAccion(currentUser?.email, userRole, 'Onboarding', 'Completó configuración inicial', datosClinica.nombre, clinicaId);
      onCompletado();
    } catch (e) { setError('Error al finalizar: ' + e.message); }
    setGuardando(false);
  }

  function avanzar() {
    setError('');
    if (paso === 1) {
      const err = validarPaso1();
      if (err) { setError(err); return; }
      setPaso(2);
    } else if (paso === 2) {
      const err = validarPaso2();
      if (err) { setError(err); return; }
      guardarPaso1Y2();
    }
  }

  return (
    <div style={{ position: 'fixed', inset: 0, background: theme.bg, zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '24px', fontFamily: "'Montserrat', sans-serif", overflowY: 'auto' }}>
      <div style={{ background: theme.bgCard, borderRadius: '16px', padding: '40px', width: '100%', maxWidth: '620px', border: `0.5px solid ${theme.border}`, boxShadow: '0 8px 40px rgba(0,0,0,0.4)' }}>

        <div style={{ textAlign: 'center', marginBottom: '8px' }}>
          <div style={{ fontSize: '20px', fontWeight: '700', color: theme.primary }}>Bienvenido a Deluxe Software</div>
          <div style={{ fontSize: '13px', color: theme.secondary, marginTop: '4px' }}>Completá estos pasos para empezar a usar el sistema</div>
        </div>

        <div style={{ display: 'flex', gap: '8px', margin: '28px 0' }}>
          {[1, 2, 3].map(p => (
            <div key={p} style={{ flex: 1, height: '4px', borderRadius: '99px', background: paso >= p ? theme.accent : theme.border, transition: 'background 0.3s' }} />
          ))}
        </div>

        {paso === 1 && (
          <div>
            <div style={{ fontSize: '15px', fontWeight: '700', color: theme.accent, marginBottom: '4px' }}>Paso 1 de 3</div>
            <div style={{ fontSize: '17px', fontWeight: '600', color: theme.primary, marginBottom: '20px' }}>Datos de tu clínica o consultorio</div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
              <div>
                <label style={labelStyle}>NOMBRE COMERCIAL *</label>
                <input value={datosClinica.nombre} onChange={e => setDatosClinica({...datosClinica, nombre: e.target.value})} placeholder="Clínica Dr. Acevedo o Consultorio Dr. Acevedo..." style={inputStyle} />
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                <div><label style={labelStyle}>TELÉFONO *</label><input value={datosClinica.telefono} onChange={e => setDatosClinica({...datosClinica, telefono: e.target.value})} style={inputStyle} /></div>
                <div><label style={labelStyle}>SITIO WEB</label><input value={datosClinica.web} onChange={e => setDatosClinica({...datosClinica, web: e.target.value})} placeholder="Opcional" style={inputStyle} /></div>
              </div>
              <div><label style={labelStyle}>DIRECCIÓN *</label><input value={datosClinica.direccion} onChange={e => setDatosClinica({...datosClinica, direccion: e.target.value})} style={inputStyle} /></div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                <div><label style={labelStyle}>CIUDAD *</label><input value={datosClinica.ciudad} onChange={e => setDatosClinica({...datosClinica, ciudad: e.target.value})} style={inputStyle} /></div>
                <div><label style={labelStyle}>PROVINCIA *</label><input value={datosClinica.provincia} onChange={e => setDatosClinica({...datosClinica, provincia: e.target.value})} style={inputStyle} /></div>
              </div>
              <div>
                <label style={labelStyle}>CONDICIÓN IMPOSITIVA *</label>
                <div style={{ display: 'flex', gap: '8px' }}>
                  {[['monotributo', 'Monotributista'], ['responsable_inscripto', 'Responsable Inscripto']].map(([v, l]) => (
                    <button key={v} onClick={() => setDatosClinica({...datosClinica, condicionImpositiva: v})} style={{ flex: 1, padding: '10px', borderRadius: '8px', cursor: 'pointer', border: datosClinica.condicionImpositiva === v ? 'none' : `0.5px solid ${theme.borderInput}`, background: datosClinica.condicionImpositiva === v ? theme.accent : 'transparent', color: datosClinica.condicionImpositiva === v ? '#FFFFFF' : theme.secondary, fontFamily: "'Montserrat', sans-serif", fontWeight: '600', fontSize: '13px' }}>
                      {l}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label style={labelStyle}>CUIT / CUIL *</label>
                <input value={datosClinica.cuitCuil} onChange={e => { const v = e.target.value.replace(/\D/g, '').slice(0, 11); setDatosClinica({...datosClinica, cuitCuil: v}); }} placeholder="20397047564 (sin guiones)" style={inputStyle} maxLength={11} />
              </div>
              <div style={{ fontSize: '11px', color: theme.secondary, opacity: 0.8, lineHeight: '1.5' }}>
                Estos datos se solicitan para futuras facturas del servicio.
              </div>
            </div>
          </div>
        )}

        {paso === 2 && (
          <div>
            <div style={{ fontSize: '15px', fontWeight: '700', color: theme.accent, marginBottom: '4px' }}>Paso 2 de 3</div>
            <div style={{ fontSize: '17px', fontWeight: '600', color: theme.primary, marginBottom: '12px' }}>Usuarios y médicos</div>

            <div style={{ background: theme.infoBg, borderRadius: '10px', padding: '14px 16px', marginBottom: '20px', fontSize: '12px', color: theme.accent, lineHeight: '1.6' }}>
              Este primer usuario (Gestión General) posee todas las funciones médicas. Solo debés consignar el nombre del médico principal. Si querés agregar más médicos, después podés crear usuarios con rol Médico desde el módulo Usuarios.
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <div>
                <label style={labelStyle}>MÉDICO PRINCIPAL *</label>
                <input value={medicos.medico1} onChange={e => setMedicos({...medicos, medico1: e.target.value})} placeholder="Nombre completo del médico..." style={inputStyle} />
              </div>
              <div>
                <label style={labelStyle}>MÉDICO 2 (opcional)</label>
                <input value={medicos.medico2} onChange={e => setMedicos({...medicos, medico2: e.target.value})} style={inputStyle} />
              </div>
              <div>
                <label style={labelStyle}>MÉDICO 3 (opcional)</label>
                <input value={medicos.medico3} onChange={e => setMedicos({...medicos, medico3: e.target.value})} style={inputStyle} />
              </div>
            </div>
          </div>
        )}

        {paso === 3 && (
          <div>
            <div style={{ fontSize: '15px', fontWeight: '700', color: theme.accent, marginBottom: '4px' }}>Paso 3 de 3</div>
            <div style={{ fontSize: '17px', fontWeight: '600', color: theme.primary, marginBottom: '20px' }}>Términos y condiciones</div>

            <div style={{ background: theme.bg, border: `0.5px solid ${theme.border}`, borderRadius: '10px', padding: '20px', marginBottom: '20px' }}>
              <div style={{ fontSize: '13px', color: theme.secondary, lineHeight: '1.7', marginBottom: '14px' }}>
                Antes de comenzar a usar Deluxe Software, necesitamos que leas y aceptes nuestros términos y condiciones generales de uso del servicio.
              </div>
              <a href={TERMINOS_URL} target="_blank" rel="noopener noreferrer" style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', color: theme.accent, fontSize: '13px', fontWeight: '600', textDecoration: 'none' }}>
                📄 Ver términos y condiciones
              </a>
            </div>

            <label style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', cursor: 'pointer' }}>
              <input type="checkbox" checked={terminosAceptados} onChange={e => setTerminosAceptados(e.target.checked)} style={{ marginTop: '2px' }} />
              <span style={{ fontSize: '13px', color: theme.primary, lineHeight: '1.6' }}>
                He leído y acepto los términos y condiciones generales de uso del servicio de Deluxe Software.
              </span>
            </label>
          </div>
        )}

        {error && (
          <div style={{ background: theme.errorBg, color: theme.error, fontSize: '13px', padding: '10px 14px', borderRadius: '8px', marginTop: '16px' }}>
            {error}
          </div>
        )}

        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '28px' }}>
          {paso > 1 ? (
            <button onClick={() => { setError(''); setPaso(paso - 1); }} style={btnSecondaryStyle}>Atrás</button>
          ) : <div />}
          {paso < 3 ? (
            <button onClick={avanzar} disabled={guardando} style={btnPrimaryStyle}>{guardando ? 'Guardando...' : 'Continuar'}</button>
          ) : (
            <button onClick={finalizarOnboarding} disabled={guardando} style={btnPrimaryStyle}>{guardando ? 'Finalizando...' : 'Finalizar y comenzar a usar Deluxe'}</button>
          )}
        </div>
      </div>
    </div>
  );
}